import { Browser, OpenOptions } from "@capacitor/browser";
import { PluginListenerHandle } from "@capacitor/core";
import { isMobileApp } from "../platform-detection";
import { getBootstrapData } from "@ui/bootstrap-data/bootstrap-data-store";

let handleLinkClick: ((event: Event) => Promise<boolean | void>) | undefined;
let browserFinishedHandle: PluginListenerHandle | undefined;
let isBrowserOpening = false;
let isBrowserPresented = false;
let internalRouteNavigate:
  | ((to: string, options?: { replace?: boolean; state?: unknown }) => void)
  | undefined;

type AppBrowserListeners = {
  onBrowserFinished?: () => void;
  onBrowserPageLoaded?: () => void;
};
interface OpenUrlProps extends AppBrowserListeners {
  url: string;
}

type BrowserUrlTarget =
  | { type: "internal"; route: string }
  | { type: "external"; url: string };

const DEBUG_APP_BROWSER = false;

function logAppBrowser(message: string, payload?: unknown) {
  if (!DEBUG_APP_BROWSER || typeof console === "undefined") {
    return;
  }

  if (typeof payload === "undefined") {
    console.info(message);
  } else {
    console.info(message, payload);
  }
}

const addListener = (listeners?: AppBrowserListeners) => {
  if (!isMobileApp() || handleLinkClick) return;

  handleLinkClick = async (event: Event): Promise<boolean | void> => {
    if (event.defaultPrevented) {
      logAppBrowser("[AppBrowser] ignored anchor click", {
        reason: "event already prevented",
      });
      return;
    }

    const trigger = (event.target as HTMLElement)?.closest("a");
    if (!trigger) return;

    const hrefAttr = trigger.getAttribute("href");
    const hrefProp = trigger.href;
    const rawUrl = (hrefProp || hrefAttr || "").trim();

    logAppBrowser("[AppBrowser] anchor click detected", {
      hrefAttr,
      hrefProp,
      target: trigger.getAttribute("target"),
      origin: window.location.origin,
    });

    if (rawUrl) {
      const target = resolveBrowserUrlTarget(rawUrl);
      if (!target) {
        logAppBrowser("[AppBrowser] ignored anchor click", {
          hrefAttr,
          reason: "unsupported url",
        });
        return;
      }

      event.preventDefault();
      await openUrl({
        url: rawUrl,
        onBrowserFinished: listeners?.onBrowserFinished,
        onBrowserPageLoaded: listeners?.onBrowserPageLoaded,
      });
      return false;
    }

    logAppBrowser("[AppBrowser] ignored anchor click", {
      hrefAttr,
      reason: "missing href",
    });
  };

  document.addEventListener("click", handleLinkClick);
};

const removeListener = () => {
  if (!isMobileApp()) return;

  if (handleLinkClick) {
    document.removeEventListener("click", handleLinkClick);
    handleLinkClick = undefined;
  }

  browserFinishedHandle?.remove();
  browserFinishedHandle = undefined;
  isBrowserOpening = false;
  isBrowserPresented = false;

  const removeAllListeners = async () => {
    await Browser.removeAllListeners();
  };

  removeAllListeners();
};

interface openProps {
  options: OpenOptions;
  onBrowserFinished?: () => void;
  onBrowserPageLoaded?: () => void;
}

const open = async (props: openProps) => {
  if (!isMobileApp()) return;

  if (isBrowserOpening || isBrowserPresented) {
    logAppBrowser("[AppBrowser] ignoring duplicate open", {
      url: props.options.url,
      isBrowserOpening,
      isBrowserPresented,
    });
    return;
  }

  await ensureBrowserFinishedListener();

  const safeOptions = {
    ...props.options,
    url: getBrowserSafeUrl(props.options.url),
  };
  let finishedHandle: PluginListenerHandle | undefined;
  let pageLoadedHandle: PluginListenerHandle | undefined;

  if (props.onBrowserFinished || props.onBrowserPageLoaded) {
    finishedHandle = await Browser.addListener("browserFinished", () => {
      if (props.onBrowserFinished) {
        props.onBrowserFinished();
      }
      finishedHandle?.remove();
      pageLoadedHandle?.remove();
    });

    // BrowserPageLoaded
    if (props.onBrowserPageLoaded) {
      pageLoadedHandle = await Browser.addListener(
        "browserPageLoaded",
        props.onBrowserPageLoaded,
      );
    }
  }

  isBrowserOpening = true;
  isBrowserPresented = true;

  try {
    await tryOpenBrowser(safeOptions);
  } catch (error) {
    isBrowserOpening = false;
    isBrowserPresented = false;
    finishedHandle?.remove();
    pageLoadedHandle?.remove();
    throw error;
  }

  isBrowserOpening = false;
};

const openUrl = async (props: OpenUrlProps) => {
  const target = resolveBrowserUrlTarget(props.url);
  if (!target) {
    return false;
  }

  if (target.type === "internal") {
    logAppBrowser("[AppBrowser] opening internal route", {
      rawUrl: props.url,
      route: target.route,
    });
    openInternalRoute(target.route);
    return true;
  }

  logAppBrowser("[AppBrowser] opening external url", {
    rawUrl: props.url,
    safeUrl: target.url,
  });

  await open({
    options: {
      url: target.url,
      presentationStyle: "fullscreen",
    },
    onBrowserFinished: props.onBrowserFinished,
    onBrowserPageLoaded: props.onBrowserPageLoaded,
  });

  return true;
};

function getBrowserSafeUrl(url: string): string {
  const sanitizedUrl = url.replace(/%(?![0-9A-Fa-f]{2})/g, "%25");

  try {
    return new URL(sanitizedUrl).toString();
  } catch {
    return encodeURI(sanitizedUrl);
  }
}

function resolveBrowserUrlTarget(rawUrl: string): BrowserUrlTarget | null {
  const normalizedUrl = rawUrl.trim();
  if (!normalizedUrl) return null;

  if (isInternalRouteLike(normalizedUrl)) {
    return {
      type: "internal",
      route: normalizeInternalRoute(normalizedUrl),
    };
  }

  const safeUrl = getBrowserSafeUrl(normalizedUrl);

  try {
    const parsed = new URL(safeUrl);
    if (!isHttpUrl(parsed)) {
      return null;
    }

    if (isInternalOrigin(parsed.origin)) {
      return {
        type: "internal",
        route: `${parsed.pathname}${parsed.search}${parsed.hash}`,
      };
    }

    return {
      type: "external",
      url: parsed.toString(),
    };
  } catch {
    return null;
  }
}

function isInternalRouteLike(url: string) {
  return (
    url.startsWith("/") ||
    url.startsWith("./") ||
    url.startsWith("../") ||
    url.startsWith("?") ||
    url.startsWith("#")
  );
}

function normalizeInternalRoute(route: string) {
  if (route.startsWith("?") || route.startsWith("#")) {
    return `${window.location.pathname}${route}`;
  }

  try {
    const parsed = new URL(route, window.location.origin);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return route;
  }
}

function isHttpUrl(url: URL) {
  return url.protocol === "http:" || url.protocol === "https:";
}

function isInternalOrigin(origin: string) {
  const internalOrigins = new Set<string>([window.location.origin]);
  const baseUrl = getBootstrapData().settings.base_url?.trim();

  if (baseUrl) {
    try {
      internalOrigins.add(new URL(baseUrl).origin);
    } catch {
      // Ignore invalid base url config and keep current origin only.
    }
  }

  return internalOrigins.has(origin);
}

function openInternalRoute(route: string) {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (internalRouteNavigate) {
    internalRouteNavigate(route, {
      state: {
        prevPath: currentPath,
        appBrowserPresentation: "stacked",
      },
    });
    return;
  }

  window.location.assign(route);
}

const close = () => {
  if (!isMobileApp()) return;

  isBrowserOpening = false;
  isBrowserPresented = false;

  Browser.close();
};

async function ensureBrowserFinishedListener() {
  if (!isMobileApp() || browserFinishedHandle) return;

  browserFinishedHandle = await Browser.addListener("browserFinished", () => {
    isBrowserOpening = false;
    isBrowserPresented = false;
  });
}

async function tryOpenBrowser(options: OpenOptions) {
  try {
    await Browser.open(options);
  } catch (error) {
    if (!isUnableToDisplayUrlError(error)) {
      throw error;
    }

    console.warn("[AppBrowser] retrying after stale browser state", {
      url: options.url,
      error: String(error),
    });

    await resetBrowserState();
    await Browser.open(options);
  }
}

function isUnableToDisplayUrlError(error: unknown) {
  return String(error).includes("Unable to display URL");
}

async function resetBrowserState() {
  isBrowserOpening = false;
  isBrowserPresented = false;

  try {
    await Browser.close();
  } catch {
    // Ignore "No active window to close!" and similar stale-state failures.
  }
}

export const AppBrowser = {
  open,
  openUrl,
  close,
  addListener,
  removeListener,
  setNavigate: (
    navigate?: (
      to: string,
      options?: { replace?: boolean; state?: unknown },
    ) => void,
  ) => {
    internalRouteNavigate = navigate;
  },
};
